'use client';

import { Fragment, useRef, useState } from 'react';
import { ListingReview } from '@/utils/platform-api/__generated__/graphql';
import { pluralize } from '@/utils/pluralize';
import {
  CloseButton,
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
} from '@headlessui/react';
import { ClientBrandConfig } from 'types/brand-config';

// import Select from '../../ui/select-input';
import Review from './review';
import {
  DefaultReviewSortKey,
  // ReviewSortKey,
  useReviewSort,
} from './useReviewSort';

const Reviews: React.FC<{
  reviews: ListingReview[];
  brand: ClientBrandConfig;
  averageRating: number | undefined;
  id?: string;
}> = ({ reviews, brand, averageRating, id }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [sort /*setSort*/] = useState(DefaultReviewSortKey);
  const sortedReviews = useReviewSort(reviews, sort);
  const hasReviews = Array.isArray(reviews) && reviews.length > 0;

  if (!hasReviews) {
    return (
      <div className="ut-md p-0 text-left text-black-1" id={id}>
        <span className="flex items-center text-inherit">No reviews.</span>
      </div>
    );
  }

  return (
    <>
      <div className="ut-md group p-0 text-left text-black-1" id={id}>
        <span className="flex items-center text-inherit">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width=".8rem"
            height=".8rem"
            viewBox="0 0 21 21"
            className="mr-1.5"
          >
            <path
              d="M20.9702 8.25086C20.9702 7.93995 20.7351 7.74675 20.2642 7.67111L13.9381 6.75121L11.1027 1.01713C10.9432 0.67265 10.7371 0.500488 10.4852 0.500488C10.2332 0.500488 10.0273 0.67265 9.86767 1.01713L7.03203 6.75121L0.705662 7.67111C0.235272 7.74675 0 7.93995 0 8.25086C0 8.42735 0.105029 8.62906 0.315088 8.85583L4.90236 13.317L3.81865 19.6182C3.80179 19.7359 3.79344 19.82 3.79344 19.8703C3.79344 20.0468 3.83752 20.1958 3.92569 20.3178C4.01386 20.4397 4.14611 20.5005 4.32261 20.5005C4.47389 20.5005 4.64187 20.4504 4.82672 20.3494L10.485 17.375L16.1438 20.3494C16.3204 20.4502 16.4884 20.5005 16.6477 20.5005C16.8162 20.5005 16.9441 20.4399 17.0323 20.3178C17.1203 20.1959 17.1644 20.0468 17.1644 19.8703C17.1644 19.7611 17.1602 19.6771 17.1517 19.6182L16.0678 13.317L20.6424 8.85583C20.8611 8.63741 20.9702 8.43571 20.9702 8.25086Z"
              fill="currentColor"
            />
          </svg>
          <span className="text-inherit">
            {averageRating ? averageRating.toFixed(1) : ''}&nbsp;&middot;&nbsp;
          </span>
          <button
            className="ut-md group !p-0 text-left !font-semibold text-secondary-main"
            id={id}
            type="button"
            onClick={() => setOpen(true)}
          >
            <span className="flex items-center text-inherit hover:underline">
              See {reviews.length > 1 && `all ${reviews.length} `}
              {pluralize('review', reviews.length)}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="ml-1.5 h-[16px] w-[8px] sm:w-[10px]"
                viewBox="0 0 10 16"
                fill="none"
              >
                <path
                  d="M2.13672 13.7273L7.86399 8.00002L2.13672 2.27275"
                  stroke="currentColor"
                  strokeWidth="2.45455"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </button>
        </span>
      </div>
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          window.history.pushState({}, '', window.location.pathname);
        }}
        as="div"
        className="relative"
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 z-50 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>
        <div className="fixed bottom-0 z-50 sm:-top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:translate-y-1/2 sm:transform">
          <div className="w-screen max-w-screen-sm">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-100"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-100"
            >
              <DialogPanel className="relative w-full transform rounded-t-xl bg-white text-left shadow-xl transition-all sm:overflow-hidden sm:rounded-xl dark:bg-gray-900 sm:p-1">
                <div className="flex w-full flex-row items-center justify-between gap-3 px-4 pb-2 pt-3 sm:pt-4">
                  <DialogTitle className="ut-lg m-0 min-w-0 flex-1 font-semibold text-black-1 dark:text-white">
                    {reviews.length > 0 && `${reviews.length}`}&nbsp;
                    {pluralize('Review', reviews.length)}
                  </DialogTitle>
                  <CloseButton
                    type="button"
                    aria-label="Close"
                    className="-m-1 shrink-0 rounded-md p-1 text-gray-6 hover:text-gray-8 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-main"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path
                        fill="currentColor"
                        d="M18.3 5.71a.996.996 0 0 0-1.41 0L12 10.59L7.11 5.7A.996.996 0 1 0 5.7 7.11L10.59 12L5.7 16.89a.996.996 0 1 0 1.41 1.41L12 13.41l4.89 4.89a.996.996 0 1 0 1.41-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4"
                      />
                    </svg>
                  </CloseButton>
                </div>
                <div
                  ref={scrollRef}
                  className="group max-h-[92dvh] overflow-y-scroll px-4 pb-4 pt-2 sm:max-h-[50dvh]"
                >
                  <div className="flex animate-fade-in flex-col gap-6">
                    {sortedReviews.map((review, index) => (
                      <Review
                        key={index}
                        review={review}
                        currentView={true}
                        brand={brand}
                      />
                    ))}
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default Reviews;
